import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Wand2, ExternalLink, MapPin } from "lucide-react";
import { toast } from "sonner";
import { VENTURE_BUILDERS, ACCELERATORS, INCUBATORS } from "@/data/aspiringData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function LaunchTab({ profile, update }) {
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/aspiring/next-steps`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ profile })
      });
      if (r.ok) { 
        const data = await r.json();
        update("next_steps", data.next_steps); 
        toast.success("Generated!"); 
      }
    } catch { toast.error("Failed"); }
    setGenerating(false);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Making the Jump</h2>
      <Card><CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Employment</Label>
            <Select value={profile.current_employment || ""} onValueChange={v => update("current_employment", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="employed">Employed</SelectItem><SelectItem value="academic">Academic</SelectItem><SelectItem value="between">Between jobs</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Runway</Label>
            <Select value={profile.months_runway || ""} onValueChange={v => update("months_runway", v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="0-6">0-6 months</SelectItem><SelectItem value="6-12">6-12</SelectItem><SelectItem value="12+">12+</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Risk Tolerance</Label>
          <Select value={profile.risk_tolerance || ""} onValueChange={v => update("risk_tolerance", v)}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
          </Select>
        </div>
      </CardContent></Card>
      
      <Card><CardHeader><CardTitle className="text-base">Resources</CardTitle></CardHeader><CardContent>
        <Tabs defaultValue="builders">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="builders">Builders</TabsTrigger>
            <TabsTrigger value="accelerators">Accelerators</TabsTrigger>
            <TabsTrigger value="incubators">Incubators</TabsTrigger>
          </TabsList>
          <TabsContent value="builders"><ResourceGrid items={VENTURE_BUILDERS} /></TabsContent>
          <TabsContent value="accelerators"><ResourceGrid items={ACCELERATORS} /></TabsContent>
          <TabsContent value="incubators"><ResourceGrid items={INCUBATORS} /></TabsContent>
        </Tabs>
      </CardContent></Card>

      <Card><CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-base">Next Steps</CardTitle>
          <Button onClick={generate} disabled={generating} size="sm" className="bg-amber-500 hover:bg-amber-600">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}Generate
          </Button>
        </div>
      </CardHeader><CardContent>
        {profile.next_steps ? (
          <div className="space-y-3 text-sm">
            {profile.next_steps.immediate_actions && <div><h4 className="font-semibold mb-1">Next 30 Days</h4><ul>{profile.next_steps.immediate_actions.map((a, i) => <li key={i}>{i+1}. {a}</li>)}</ul></div>}
            {profile.next_steps.people_to_contact && <div><h4 className="font-semibold mb-1">Contact</h4><ul>{profile.next_steps.people_to_contact.map((p, i) => <li key={i}>→ {p}</li>)}</ul></div>}
            {profile.next_steps.recommended_path && <div className="p-3 bg-amber-50 rounded">{profile.next_steps.recommended_path}</div>}
          </div>
        ) : <p className="text-center text-slate-400 py-4">Click Generate for guidance</p>}
      </CardContent></Card>
    </div>
  );
}

function ResourceGrid({ items }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map((r, i) => (
        <Card key={i} className="border">
          <CardContent className="p-3">
            <div className="flex justify-between mb-1">
              <span className="font-semibold text-sm">{r.name}</span>
              {r.url && <a href={r.url} target="_blank" className="text-blue-600"><ExternalLink className="w-3 h-3" /></a>}
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{r.location}</p>
            <p className="text-xs text-slate-600 mt-1">{r.desc}</p>
            {r.funding && <p className="text-xs text-amber-600 font-medium mt-1">{r.funding}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
